import { useEffect, useRef } from "react";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import type { InteractionTopicInput } from "../queries";
import { isTopicLocked, type TopicFieldChanges, type TopicValidationErrors } from "../_utils/topicsValidation";

export type TopicFlash = {
  key: number;
  dept: string;
  changes: TopicFieldChanges;
  deleted: boolean;
} | null;

type TopicsEditorProps = {
  topics: InteractionTopicInput[];
  errors: TopicValidationErrors;
  focusKey: string | null;
  flash: TopicFlash;
  onFocusApplied: () => void;
  onChangeTopicName: (topicIndex: number, name: string) => void;
  onChangeSubtopics: (topicIndex: number, text: string) => void;
  onAddTopic: () => void;
  onRemoveTopic: (topicIndex: number) => void;
};

export function topicFocusKey(topicIndex: number) {
  return `topic:${topicIndex}`;
}

function subtopicRows(lines: string[]): number {
  // Empty boxes stay a single line tall so unscrolled departments read as a
  // compact list; boxes with content keep a spare row for the next subtopic.
  const hasContent = lines.some((line) => line.trim().length > 0);
  if (!hasContent) return 1;
  return Math.min(lines.length + 1, 12);
}

function SavedBadge({ flashKey, testId }: { flashKey: number; testId: string }) {
  return (
    <span
      key={flashKey}
      role="status"
      data-testid={testId}
      className="topics-save-flash pointer-events-none absolute -top-3 right-2 z-10 rounded-full border border-sys-brand-outline-variant bg-sys-brand-surface px-2 py-0.5 text-body-small font-medium text-sys-brand-primary shadow-sm"
    >
      ✓ Saved
    </span>
  );
}

export default function TopicsEditor({
  topics,
  errors,
  focusKey,
  flash,
  onFocusApplied,
  onChangeTopicName,
  onChangeSubtopics,
  onAddTopic,
  onRemoveTopic
}: TopicsEditorProps) {
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    if (!focusKey) return;
    const input = inputRefs.current.get(focusKey);
    if (input) {
      input.focus();
      input.select();
    }
    onFocusApplied();
  }, [focusKey, onFocusApplied, topics]);

  const setRef = (key: string) => (element: HTMLInputElement | null) => {
    if (element) {
      inputRefs.current.set(key, element);
    } else {
      inputRefs.current.delete(key);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {errors.formError ? (
        <p role="alert" data-testid="topics-form-error" className="text-body-medium text-sys-brand-error">
          {errors.formError}
        </p>
      ) : null}
      <div
        data-testid="topics-grid"
        className="grid grid-cols-1 items-start gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {topics.map((topic, topicIndex) => {
          // Locked Other rows render as static footer lines (below), never as
          // disabled inputs — state indexes are preserved for keys/handlers.
          if (isTopicLocked(topics, topicIndex)) return null;
          const topicError = errors.topicErrors[topicIndex];
          const subtopicError = errors.subtopicErrors[topicIndex];
          // State holds the raw textarea lines; trimming and the canonical
          // Other happen at save serialization, never while typing.
          const lines = topic.subtopics;
          const hasSubtopics = lines.some((line) => line.trim().length > 0);
          const flashFields = flash?.changes[topicIndex];
          return (
            <div
              key={topicIndex}
              data-testid={`topic-card-${topicIndex}`}
              className="flex flex-col gap-6 rounded-lg bg-sys-brand-surface-container-low p-4"
            >
              <div className="relative flex-1">
                {/* w-full on both fields: the Material host is inline-flex
                    shrink-to-fit and these wrappers are not flex containers, so
                    flex-1 is inert and input/textarea intrinsic widths differ. */}
                <TextField
                  ref={setRef(topicFocusKey(topicIndex))}
                  label="Topic"
                  value={topic.name}
                  onChangeText={(value) => onChangeTopicName(topicIndex, value)}
                  error={Boolean(topicError)}
                  errorText={topicError}
                  trailingIcon={
                    <IconButton
                      name="delete"
                      skipMargin
                      ariaLabel={`Remove ${topic.name || "topic"}`}
                      onClick={() => onRemoveTopic(topicIndex)}
                    />
                  }
                  className="w-full"
                />
                {flash && flashFields?.name ? (
                  <SavedBadge flashKey={flash.key} testId={`saved-badge-${topicIndex}-name`} />
                ) : null}
              </div>
              <div className="flex flex-col gap-6">
                <div className="relative flex-1">
                  <TextField
                    label="Subtopics"
                    placeholder="One subtopic per line"
                    type="textarea"
                    rows={subtopicRows(lines)}
                    value={lines.join("\n")}
                    onChangeText={(value) => onChangeSubtopics(topicIndex, value)}
                    error={Boolean(subtopicError)}
                    errorText={subtopicError}
                    className="w-full"
                  />
                  {flash && flashFields?.subtopics ? (
                    <SavedBadge flashKey={flash.key} testId={`saved-badge-${topicIndex}-subtopics`} />
                  ) : null}
                </div>
                {hasSubtopics ? (
                  <p className="text-body-small text-sys-brand-on-surface-variant">+ Subtopic: Other [always shown]</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-body-small text-sys-brand-on-surface-variant" data-testid="topics-other-footer">
        + Topic: Other [always shown]
      </p>
      <div>
        <Button variant="outlined" icon="add" onClick={onAddTopic} testId="add-topic">
          Add topic
        </Button>
      </div>
    </div>
  );
}
